/**
 * Print/PDF HTML for a block document: BlockNote's lossy semantic HTML
 * (h1–h3, p, ul/ol, table, pre, blockquote, img) wrapped in a standalone
 * stylesheet — Browser Rendering gets a self-contained page.
 */
const PRINT_STYLES = `
  @page { size: A4; margin: 2cm 2cm; }
  body {
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 15px; line-height: 1.6; color: #1a1a1a; background: #fff; margin: 0; padding: 0;
    -webkit-print-color-adjust: exact;
    /* Matches the on-screen size once page margins are applied. */
    zoom: 0.95;
  }
  h1 { font-size: 2rem; font-weight: 700; line-height: 1.2; margin: 1.5rem 0 1rem; }
  h2 { font-size: 1.5rem; font-weight: 600; line-height: 1.3; margin: 1.5rem 0 .75rem; }
  h3 { font-size: 1.25rem; font-weight: 600; line-height: 1.4; margin: 1.25rem 0 .5rem; }
  p { margin: 0 0 .9rem; }
  ul, ol { padding-left: 1.5rem; margin: 0 0 1rem; }
  li { margin-bottom: .3rem; }
  /* Quote and table mirror BlockNote's editor CSS so print matches the screen. */
  blockquote { border-left: 2px solid rgb(125, 121, 122); color: rgb(125, 121, 122); padding-left: 1em; margin: 0 0 .9rem; }
  pre { background: #f4f4f4; padding: .75rem 1rem; border-radius: 6px; font-size: .85rem; overflow-x: auto; white-space: pre-wrap; }
  code { font-family: Menlo, Consolas, monospace; font-size: .9em; }
  table { border-collapse: collapse; width: auto; max-width: 100%; margin: 0 0 .9rem; word-break: break-word; }
  th, td { border: 1px solid #ddd; padding: 5px 10px; text-align: left; vertical-align: top; }
  th { font-weight: 700; }
  th > p, td > p { margin: 0; min-height: 1.5rem; }
  tr { break-inside: avoid; }
  img { max-width: 100%; height: auto; }
  hr { border: 0; border-top: 1px solid #d4d4d4; margin: 1.5rem 0; }
  a { color: #171717; }
  [data-page-break] { page-break-after: always; break-after: page; height: 0; }
`

export function buildPrintHtml(bodyHtml: string, title?: string): string {
  const safeTitle = (title ?? 'Dokument').replace(/[<>&]/g, '')
  return `<!doctype html>
<html lang="sv">
<head>
<meta charset="utf-8" />
<title>${safeTitle}</title>
<style>${PRINT_STYLES}</style>
</head>
<body>
${bodyHtml}
</body>
</html>`
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * BlockNote fills empty blocks with U+FFFC (object replacement character) so
 * they survive an HTML round trip; print engines render it as a boxed "OBJ".
 * Swap it for a `<br>` so the blank line still takes up space.
 */
const EMPTY_BLOCK_PLACEHOLDER = '\uFFFC'

/**
 * Make BlockNote's external HTML self-contained for print/PDF: strip the
 * empty-block placeholder and replace every `<img src>` with a data URI. The
 * PDF is rendered by a remote headless browser that can neither resolve
 * relative workspace URLs nor send our session cookie — so the bytes travel
 * inside the HTML.
 */
export async function inlineImages(html: string): Promise<string> {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
  const placeholders: Text[] = []
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if ((n as Text).data.includes(EMPTY_BLOCK_PLACEHOLDER)) placeholders.push(n as Text)
  }
  for (const text of placeholders) {
    const rest = text.data.replaceAll(EMPTY_BLOCK_PLACEHOLDER, '')
    if (rest.trim()) text.data = rest
    else text.replaceWith(doc.createElement('br'))
  }
  const images = [...doc.querySelectorAll('img')]
  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute('src')
      if (!src || src.startsWith('data:')) return
      try {
        const res = await fetch(src, { credentials: 'include' })
        if (!res.ok) throw new Error(String(res.status))
        const blob = await res.blob()
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(String(reader.result))
          reader.onerror = () => reject(reader.error)
          reader.readAsDataURL(blob)
        })
        img.setAttribute('src', dataUrl)
      } catch {
        // Unreachable image: drop it rather than print a broken icon.
        img.remove()
      }
    })
  )
  return doc.body.innerHTML
}

/**
 * Print a standalone HTML page through a hidden iframe. A popup would be
 * blocked by Safari (and strict Chrome settings) because the call happens
 * after the async image inlining, outside the click's activation window.
 */
export function printHtml(html: string): void {
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
  frame.srcdoc = html
  frame.onload = () => {
    const win = frame.contentWindow
    if (!win) return
    win.addEventListener('afterprint', () => setTimeout(() => frame.remove(), 500))
    // Let images/fonts settle before the dialog snapshots the page.
    setTimeout(() => {
      win.focus()
      win.print()
    }, 300)
  }
  document.body.appendChild(frame)
}
